import { t } from 'i18next';
import {
  FanguardApiContractsInteractionInteractionDecisionOperationModel,
  FanguardApiContractsSensorSensorAccessControlModeModelKeys,
  FanguardApiContractsEventReportModel,
  FanguardApiContractsAllMatchedReportModel,
  FanguardApiContractsInteractionExportReportModel,
  FanguardApiContractsEnrollmentExportModel,
} from 'src/api/generated';

import type { Report } from 'src/pages/Reports/Reports';

const browserLocale = navigator.language || 'en-US';

export const transformDashToCamel = (str: string) => {
  return str
    .split('-')
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export const capitalizeFirstLetters = (str: string) => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const splitCamelCase = (str: string) => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
};

export const splitCamelCaseTrimmed = (str: string) => {
  return str.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
};

export const getBase64 = (file: File | Blob) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const getInteractionType = (
  interactionType?: string | null,
  interactionOperation?: FanguardApiContractsInteractionInteractionDecisionOperationModel | null | undefined,
) => {
  switch (interactionType?.toLowerCase()) {
    case 'interactiondecisionaccesscontrol':
      return t('interactions.local');
    case 'interactiondecisioncredentialing':
      return interactionOperation?.toLowerCase() === 'credentialingcheckin'
        ? t('interactions.checkIn')
        : t('interactions.scan');
    case 'interactiondecisionwebview':
      return t('interactions.webView');
    default:
      return '';
  }
};

export const dateFormatter = (date: Date | string | null | undefined, precice: boolean = false) => {
  if (!date) return '';

  return new Intl.DateTimeFormat(browserLocale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: precice ? 'numeric' : undefined,
  }).format(new Date(date));
};

export const hasDatePassed = (date: Date | string | null | undefined) => {
  if (!date) return false;
  return new Date(date) < new Date();
};

export const dateFormatterWithAT = (date: string | null | undefined) => {
  if (!date) return '';

  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(new Date(date));

  const formattedTime = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).format(new Date(date));

  return `${formattedDate} at ${formattedTime}`;
};

export const currencyFormatter = (value: number | undefined | null) => {
  return new Intl.NumberFormat(browserLocale, {
    style: 'currency',
    currency: 'USD',
  }).format(!value ? 0 : value);
};

export const numberFormatter = (value: number | string | undefined | null) => {
  if (!value) return '';

  const locale = 'en-US';

  return new Intl.NumberFormat(locale, {
    useGrouping: true,
  }).format(Number(value));
};

export const formatProgramLabel = (program: string | null | undefined): string => {
  if (!program || typeof program !== 'string') return 'N/A';
  if (program === 'IdVerification') return 'ID Verification';
  if (program === 'AccessControl') return 'Access Control';

  const initialReplace = program.replace(/Id([A-Z])/g, 'ID $1');
  if (!initialReplace) return 'N/A';

  return initialReplace.replace(/([a-z])([A-Z])/g, '$1 $2') || 'N/A';
};

export const dateYYYYMMDD = (date: Date | string | null | undefined) => {
  if (!date) return '';

  return new Intl.DateTimeFormat(browserLocale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(new Date(date));
};

export const dateYYYYMMDDHHMM = (date: Date | string | null | undefined) => {
  if (!date) return '';

  return new Intl.DateTimeFormat(browserLocale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(date));
};

export const jobMessageDateFormatter = (date: Date | string | null | undefined) => {
  if (!date) return '';

  return new Date(date)
    .toLocaleString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(/,/g, '');
};

export const accessControlModeFormatter = (mode: FanguardApiContractsSensorSensorAccessControlModeModelKeys) => {
  switch (mode) {
    case 'DayPass':
      return t('accessControlMode.dayPass');
    case 'Ticketing':
      return t('accessControlMode.ticketing');
    case 'Credentialing':
      return t('accessControlMode.credentialing');
    case 'CredentialingCheckIn':
      return t('accessControlMode.credentialingCheckIn');
    case 'OrderFulfillment':
      return t('accessControlMode.orderFulfillment');
    case 'AgeVerification':
      return t('accessControlMode.ageVerification');
    case 'EnrollmentCheck':
      return t('accessControlMode.enrollmentCheck');
    case 'Multimode':
      return t('accessControlMode.multimode');
    case 'RemoteTicketing':
      return t('accessControlMode.remoteTicketing');
    case 'LocalSalto':
      return t('accessControlMode.localSalto');
    case 'WebView':
      return t('accessControlMode.webView');
    default:
      return t('accessControlMode.unknown');
  }
};

export const numberToPercentageFormatter = (value: number | string | undefined | null) => {
  if ((!value && value !== 0) || typeof value !== 'number') return '';

  return `${(Number(value) * 100).toFixed(2)}%`;
};

export const definitionNameFormatter = (
  definition:
    | FanguardApiContractsEventReportModel
    | FanguardApiContractsAllMatchedReportModel
    | FanguardApiContractsInteractionExportReportModel
    | FanguardApiContractsEnrollmentExportModel
    | Report,
) => {
  switch (definition.reportType) {
    case 'EventSummary':
      return t('reports.eventSummary.title');
    case 'AllMatched':
      return t('reports.allMatched.title');
    case 'InteractionExport':
      return t('reports.interactionExport.title');
    case 'Enrollment':
      return t('reports.enrollmentExport.title');
    default:
      return '';
  }
};

export const generateReportFileName = (report: Report, format: 'pdf' | 'csv') => {
  const type = definitionNameFormatter(report);
  return `${type}-${report.interactionType ? report.interactionType + '-' : ''}${new Date().toISOString().split('T')[0]}.${format}`;
};
